import { useState, useEffect } from "react";
import { Product } from "../Types/Types";


const useAdminOTC = (itemsPerPage: number, product: string) => {
  const [success, setSuccess] = useState<string | null>(null);

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1); // For page number
  const [totalCount, setTotalCount] = useState<number>(0); // Total products for pagination
  const [searchQuery, setSearchQuery] = useState<string>(""); // For search functionality
  const [sortBy, setSortBy] = useState<string>("position"); // For sorting
  const [sortOrder, setSortOrder] = useState<string>("asc"); // For sorting order
  const apiUrl = process.env.REACT_APP_API_URL;
  const url = `${apiUrl}/api/products`;

  const lang = localStorage.getItem('lang') || 'en'
  const fetchOTCProducts = async () => {
    setLoading(true);
    setError(null);

    // Get token from local storage (or another source)
    const token = localStorage.getItem("token"); // Updated to use "token" instead of "authToken"
    const encodedSearchQuery = encodeURIComponent(searchQuery);
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    // If token exists, include it in the Authorization header
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(
        `${url}/${product}?page=${currentPage}&limit=${itemsPerPage}&search=${encodedSearchQuery}&sortBy=${sortBy}&sortOrder=${sortOrder}&lang=${lang}`,
        {
          method: "GET",
          headers,
        }
      );
      const data = await response.json();

      if (response.ok) {
        setProducts(data.products); // Update careers with data
        setTotalCount(data.totalCount); // Set the total count for pagination
      } else {
        setError("Error fetching careers"); // Handle error
      }
    } catch (err) {
      setError("An error occurred while fetching careers"); // Catch and display errors
    } finally {
      setLoading(false); // Set loading to false after request
    }
  };

  // Effect to fetch careers when page or query changes
  useEffect(() => {
    if (!product) return; // Optional: prevent empty product fetch
    fetchOTCProducts();
  }, [product, currentPage, searchQuery, sortBy, sortOrder, itemsPerPage]);


  // Reset to page 1 when itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);




  const createProduct = async (formData: FormData) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${url}`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to create product");
      }
      setSuccess("Product created successfully!");  // <--- Success message
      await fetchOTCProducts(); // Refresh list
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const updateProduct = async (productId: string, formData: FormData) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${url}/${productId}`, {
        method: "PUT",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to update product");
      }
      setSuccess("Product updated successfully!");  // <--- Success message

      await fetchOTCProducts(); // Refresh list to reflect updated product
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };



  const deleteProduct = async (productId: string) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${url}/${productId}`, {
        method: "DELETE",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      });

      if (response.ok) {
        setSuccess("Product deleted successfully!");  // <--- Success message

        await fetchOTCProducts();
      } else {
        setError(error || "Error deleting career");
      }

      // Remove the deleted product from the local state
      setProducts((prevProducts) => prevProducts.filter(product => product._id !== productId));
      setTotalCount((prevTotalCount) => prevTotalCount - 1); // Decrease the total count

    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const reorder = async (newOrder: { _id: string; position: number }[]) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${url}/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(newOrder),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reorder history');
      }

      // Update local state to reflect the new order
      setProducts(prevHistory => {
        // Sort local history by the new positions from newOrder
        const orderMap = new Map(newOrder.map(item => [item._id, item.position]));
        return [...prevHistory].sort((a, b) => (orderMap.get(a._id) ?? 0) - (orderMap.get(b._id) ?? 0));
      });
      await fetchOTCProducts();
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred while reordering');
      return false;
    } finally {
      setLoading(false);
    }
  };



  return {
    products,
    loading,
    error,
    currentPage,
    setCurrentPage,
    totalCount,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    createProduct,
    updateProduct,
    deleteProduct,
    reorder,
    success,
    setSuccess
  };
};

export default useAdminOTC;
